import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { formatBC } from "@/lib/format";
import { ShoppingBag, Sparkles, Package, Truck, Tag, ChevronRight, ImageIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// -----------------------------------------------------------------------------
// PublicProduct — unauthenticated product landing page reachable via an
// operator's shared affiliate link. Format: /p/:productId?ref=<code>
//
// What it does:
//  1. Fetches the trimmed public product view (no auth required).
//  2. If a ref code is present in the URL:
//     - Resolves the operator's display name via /affiliate/resolve/{code}
//     - Fires POST /affiliate/track-click once per unique visitor+product+code
//     - Persists the code + expiry in localStorage under `zfd_ref` so it
//       auto-attaches to any order placed by the shopper for 7 days.
//  3. Shows a big "Buy on Zenith Flow" CTA that routes signed-in users to
//     /shop and unsigned visitors to /register?next=/shop (they'll see the
//     product in the grid after logging in — the ref code is already saved).
// -----------------------------------------------------------------------------
const REF_KEY = "zfd_ref";
const REF_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function saveRefCode(code) {
  if (!code) return;
  try {
    localStorage.setItem(REF_KEY, JSON.stringify({
      code, saved_at: Date.now(), expires_at: Date.now() + REF_TTL_MS,
    }));
  } catch { /* storage disabled — silently ignore */ }
}

function visitorId() {
  try {
    let v = localStorage.getItem("zfd_visitor_id");
    if (!v) {
      v = `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`.slice(0, 40);
      localStorage.setItem("zfd_visitor_id", v);
    }
    return v;
  } catch { return `v-anon-${Date.now()}`; }
}

export default function PublicProduct() {
  const { productId } = useParams();
  const [params] = useSearchParams();
  const refCode = params.get("ref");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [operator, setOperator] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${backend}/api/public/shop/${productId}`);
        setProduct(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Product not available");
      }
      if (refCode && refCode !== "preview") {
        try {
          const { data } = await axios.get(`${backend}/api/affiliate/resolve/${refCode}`);
          setOperator(data);
          saveRefCode(data.code);
          // Fire click tracking (best-effort — don't block the page)
          axios.post(`${backend}/api/affiliate/track-click`, {
            code: data.code,
            product_id: productId,
            visitor_id: visitorId(),
          }).catch(() => {});
        } catch {
          // Unknown code — still show the product, just no operator credit.
        }
      }
      setLoading(false);
    })();
  }, [productId, refCode, backend]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-white/50 text-sm" data-testid="public-product-loading">
        Loading product…
      </div>
    );
  }
  if (err || !product) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center space-y-3" data-testid="public-product-error">
        <div className="text-4xl">🕳</div>
        <h2 className="font-display text-2xl font-bold">Product not available</h2>
        <p className="text-white/60 text-sm">{err || "This item may have been removed or is temporarily off the shelf."}</p>
        <Link to="/" className="btn-deploy inline-block mt-3">Return to Zenith Flow</Link>
      </div>
    );
  }

  const imgSrc = product.has_image_data ? `${backend}${product.image_url}` : product.image_url;
  const onSale = !!product.promotional_sale;
  const salePrice = onSale
    ? +(product.price_bc * (1 - product.promotional_sale.discount_pct)).toFixed(2)
    : product.price_bc;

  const buyHref = user ? "/shop" : `/register?next=${encodeURIComponent(`/shop?highlight=${productId}`)}`;

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10" data-testid="public-product-page">
      {/* Operator credit strip */}
      {operator && (
        <div
          className="mb-6 flex items-center gap-2 px-4 py-2 rounded-md border border-[#FBBF24]/30 bg-[#FBBF24]/[0.06] text-xs"
          data-testid="operator-credit-strip"
        >
          <Sparkles size={12} className="text-[#FBBF24]" />
          <span className="text-white/70">Shared by</span>
          <strong className="text-[#FBBF24]" data-testid="operator-name">{operator.operator_name}</strong>
          <span className="text-white/40 ml-2">· They earn 2% when you buy through this link.</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Product image */}
        <div className="aspect-square bg-[#0a1526] rounded-lg overflow-hidden border border-white/10 flex items-center justify-center">
          {imgSrc ? (
            <img src={imgSrc} alt={product.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="public-product-image" />
          ) : (
            <ImageIcon size={48} className="text-white/20" />
          )}
        </div>

        {/* Details */}
        <div className="space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold">
            {product.category_label}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight" data-testid="public-product-name">
            {product.name}
          </h1>
          <p className="text-sm text-white/70 leading-relaxed max-w-md whitespace-pre-line" data-testid="public-product-description">
            {product.description || "No description provided by the seller."}
          </p>

          <div>
            {onSale ? (
              <div className="space-y-1">
                <div className="flex items-baseline gap-3">
                  <div className="font-mono-num text-4xl text-[#FBBF24]" data-testid="public-sale-price">₱{formatBC(salePrice)}</div>
                  <div className="font-mono-num text-lg text-white/40 line-through" data-testid="public-original-price">₱{formatBC(product.price_bc)}</div>
                </div>
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#DC2626]/20 text-[#FCA5A5]">
                  <Tag size={9} /> {product.promotional_sale.name} · -{Math.round(product.promotional_sale.discount_pct * 100)}%
                </div>
              </div>
            ) : (
              <div className="font-mono-num text-4xl text-[#FBBF24]" data-testid="public-price">₱{formatBC(product.price_bc)}</div>
            )}
            {product.shipping_fee_bc > 0 && (
              <div className="text-[11px] text-white/50 mt-1 inline-flex items-center gap-1">
                <Truck size={11} /> +₱{formatBC(product.shipping_fee_bc)} shipping per item
              </div>
            )}
          </div>

          <div className="text-xs text-white/50">
            {product.in_stock ? "In stock — usually ships in 24h" : "Sold out — check back soon"}
          </div>

          <button
            onClick={() => navigate(buyHref)}
            disabled={!product.in_stock}
            className="btn-deploy w-full py-3 text-base inline-flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="public-buy-cta"
          >
            <ShoppingBag size={16} /> {user ? "Open in Shop" : "Sign up & Buy on Zenith Flow"} <ChevronRight size={16} />
          </button>
          <div className="text-[10px] text-white/40 text-center">
            Secure checkout · Real-time balance ledger · Backed by sealed BC math
          </div>
        </div>
      </div>

      {/* Zenith brand footer strip */}
      <div className="mt-16 pt-6 border-t border-white/10 flex items-center justify-between text-xs text-white/40 flex-wrap gap-2">
        <Link to="/" className="hover:text-[#FBBF24] font-bold uppercase tracking-widest flex items-center gap-2">
          <Package size={12} /> Zenith Flow Dynamics
        </Link>
        <span>© {new Date().getFullYear()} ZFD · A sealed-math storefront</span>
      </div>
    </div>
  );
}
